import {
  Box,
  TextInput,
  Heading,
  Text,
  RadioGroup,
  Radio,
  Divider,
  PlusIcon,
  Button,
  TrashIcon,
  Tooltip,
  TooltipInteractiveWrapper,
  InfoIcon,
} from '@razorpay/blade/components';

import React, { useState } from 'react';
import { useFormWithProvider } from 'forms';
import {
  BenefitAmountSettings,
  BenefitAmountType,
  BenefitAmountUnit,
  DeclarationMethod,
  FlexibleBenefitFormData,
} from './types';
import { MultiBenefitValueRow, SelectAmountContainer } from './styles';
import { isEmployerNPSComponent } from './utils';

export const AmountDetails = ({ isEditFlow = false }: { isEditFlow?: boolean }) => {
  const { formData, errors, setFormData, clearAllErrors, setFieldError, clearFieldError } =
    useFormWithProvider<FlexibleBenefitFormData>();

  const [multipleOptionInputValue, setMultipleOptionInputValue] = useState('');

  const handleDeclarationSettingsChange = (value: DeclarationMethod) => {
    setFormData({
      ...formData,
      declaration_settings: {
        declaration_enabled: value,
      },
      benefit_amount_settings: {
        ...formData.benefit_amount_settings,
        ...(value === 'true'
          ? { type: 'RANGE', unit: 'ACTUAL_AMOUNT', value: 0 }
          : { type: 'FIXED', value: 0 }),
      },
    });
  };

  const formatOptions = (options: Array<number>): string => {
    if (options.length === 0) return '';
    if (options.length === 1) return options[0].toString();
    if (options.length === 2) return `${options[0]} and ${options[1]}`;
    return `${options.slice(0, -1).join(', ')} and ${options[options.length - 1]}`;
  };

  const setBenefitsAmountData = (updatedValue: BenefitAmountSettings) => {
    clearAllErrors();
    setFormData({
      ...formData,
      benefit_amount_settings: {
        ...formData.benefit_amount_settings,
        ...updatedValue,
      },
    });
  };

  const onMultipleValueInputAdd = () => {
    if (!multipleOptionInputValue) {
      setFieldError('multipleValuesList', 'Please enter a value');
      return;
    }

    if (!/^\d+$/.test(multipleOptionInputValue)) {
      setFieldError('multipleValuesList', 'Please enter a valid number without decimals');
      return;
    }

    const numericValue = Number(multipleOptionInputValue);
    if (numericValue < 0) {
      setFieldError('multipleValuesList', 'Please enter a positive number');
      return;
    }

    if (formData.benefit_amount_settings?.options?.includes(Number(multipleOptionInputValue))) {
      setFieldError('multipleValuesList', 'Value already exists');
      return;
    }

    setBenefitsAmountData({
      options: [
        ...(formData?.benefit_amount_settings?.options || []),
        Number(multipleOptionInputValue),
      ],
      default_option_index: noMultiValueOptionsAdded
        ? 0
        : formData?.benefit_amount_settings?.default_option_index,
    });
    setMultipleOptionInputValue('');
  };

  const isMultiValuesSelected =
    formData?.benefit_amount_settings?.type === 'LIST_OF_VALUES' &&
    (formData?.benefit_amount_settings?.options?.length ?? 0) > 0;

  const isPercentageOfBasicSelected =
    formData?.benefit_amount_settings?.unit === 'PERCENTAGE_OF_BASIC';

  const isFixedAmountSelected = formData?.benefit_amount_settings?.unit === 'ACTUAL_AMOUNT';

  const noMultiValueOptionsAdded = (formData?.benefit_amount_settings?.options?.length ?? 0) === 0;

  const isMaxLimitSelected = formData.benefit_amount_settings?.type === 'RANGE';

  const isAutoApplyFullAmount = formData.declaration_settings?.declaration_enabled === 'false';

  const [isHoveredOption, setIsHoveredOption] = useState<number>();

  const isNPSComponent = isEmployerNPSComponent(formData?.name || '');

  return (
    <Box padding="spacing.6" display="flex" flexDirection="column" gap="spacing.6">
      <Box>
        <Heading size="large" weight="semibold" color="surface.text.gray.normal">
          Amount Details
        </Heading>
      </Box>

      <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
        <Box display="flex" flexDirection="row">
          <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
            <Text size="large" weight="semibold" color="surface.text.gray.normal">
              Declaration Method
            </Text>
          </Box>
          <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
            <RadioGroup
              name="declaration-method"
              size="medium"
              isDisabled={isEditFlow && isNPSComponent}
              value={`${formData?.declaration_settings?.declaration_enabled}`}
              onChange={({ value }) => handleDeclarationSettingsChange(value as DeclarationMethod)}>
              <Radio value="true" helpText="Employees can declare an amount of their choice.">
                Enable Declaration
              </Radio>
              <Radio
                value="false"
                helpText="The maximum eligible amount will be applied automatically">
                Auto-Apply Full Amount
              </Radio>
            </RadioGroup>
          </Box>
        </Box>

        <Divider />

        {isAutoApplyFullAmount ? (
          <Box display="flex" flexDirection="row">
            <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
              <Text size="large" weight="semibold" color="surface.text.gray.normal">
                Define Benefit Amount
              </Text>
            </Box>
            <Box display="flex" flexDirection="column" gap="spacing.7" flex={1}>
              <RadioGroup
                name="benefit-unit"
                size="medium"
                value={formData?.benefit_amount_settings?.unit || 'ACTUAL_AMOUNT'}
                onChange={({ value }) =>
                  setBenefitsAmountData({
                    unit: value as BenefitAmountUnit,
                    value: 0,
                  })
                }>
                <Box display="flex" flexDirection="row" gap="spacing.5">
                  <SelectAmountContainer isSelected={isFixedAmountSelected}>
                    <Radio value="ACTUAL_AMOUNT">Fixed Amount</Radio>
                  </SelectAmountContainer>
                  <SelectAmountContainer isSelected={isPercentageOfBasicSelected}>
                    <Radio value="PERCENTAGE_OF_BASIC">% of Basic</Radio>
                  </SelectAmountContainer>
                </Box>
              </RadioGroup>
              <Box>
                <TextInput
                  accessibilityLabel={isPercentageOfBasicSelected ? 'Percentage' : 'Amount'}
                  placeholder={isPercentageOfBasicSelected ? '10' : '2400'}
                  prefix={isPercentageOfBasicSelected ? undefined : '₹'}
                  suffix={isPercentageOfBasicSelected ? '% of Basic' : undefined}
                  value={formData?.benefit_amount_settings?.value?.toString() || ''}
                  onChange={({ value }) =>
                    setBenefitsAmountData({
                      value,
                    })
                  }
                  name="benefit-amount"
                  size="medium"
                  validationState={
                    errors.maxLimitAmount || errors.maxLimitPercentage ? 'error' : 'none'
                  }
                  errorText={errors.maxLimitAmount || errors.maxLimitPercentage}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Box display="flex" flexDirection="row">
              <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
                <Text size="large" weight="semibold" color="surface.text.gray.normal">
                  Set Benefit Amount
                </Text>
              </Box>
              <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
                <RadioGroup
                  name="type"
                  size="medium"
                  value={formData?.benefit_amount_settings?.type || 'RANGE'}
                  isDisabled={isEditFlow && isNPSComponent}
                  onChange={({ value }) =>
                    setBenefitsAmountData({
                      type: value as BenefitAmountType,
                      ...(value === 'RANGE' ? { options: undefined } : {}),
                      ...(value === 'LIST_OF_VALUES' ? { value: 0, unit: 'ACTUAL_AMOUNT' } : {}),
                    })
                  }>
                  <Radio
                    value={
                      formData?.declaration_settings?.declaration_enabled === 'true'
                        ? 'RANGE'
                        : 'FIXED'
                    }
                    helpText="Define a maximum limit, employee can declare any value between 0 to maximum limit">
                    Set Maximum Limit
                  </Radio>
                  <Radio
                    value="LIST_OF_VALUES"
                    helpText="Define multiple values for employees to choose a single option from the dropdown.">
                    Multiple Values List
                  </Radio>
                </RadioGroup>
              </Box>
            </Box>

            <Divider />

            <Box display="flex" flexDirection="row">
              <Box flex={1}>
                <Text color="surface.text.gray.subtle" weight="semibold">
                  {isMaxLimitSelected ? 'Set a Max limit' : 'Add multiple of value '}
                </Text>
              </Box>
              {isMaxLimitSelected ? (
                <Box display="flex" flexDirection="column" gap="spacing.5" flex={1}>
                  <RadioGroup
                    name="unit"
                    size="medium"
                    value={formData?.benefit_amount_settings?.unit || 'ACTUAL_AMOUNT'}
                    isDisabled={isEditFlow && isNPSComponent}
                    onChange={({ value }) =>
                      setBenefitsAmountData({
                        unit: value as BenefitAmountUnit,
                        value: 0,
                      })
                    }>
                    <Box display="flex" flexDirection="row" gap="spacing.5">
                      <SelectAmountContainer isSelected={isFixedAmountSelected}>
                        <Radio value="ACTUAL_AMOUNT">Fixed Amount</Radio>
                      </SelectAmountContainer>
                      <SelectAmountContainer isSelected={isPercentageOfBasicSelected}>
                        <Radio value="PERCENTAGE_OF_BASIC">% of Basic</Radio>
                      </SelectAmountContainer>
                    </Box>
                  </RadioGroup>
                  <Box>
                    <TextInput
                      accessibilityLabel={isPercentageOfBasicSelected ? 'Percentage' : 'Amount'}
                      placeholder={isPercentageOfBasicSelected ? '10' : '2000'}
                      prefix={isPercentageOfBasicSelected ? undefined : '₹'}
                      suffix={isPercentageOfBasicSelected ? '% of Basic' : undefined}
                      value={formData?.benefit_amount_settings?.value?.toString() || ''}
                      onChange={({ value }) =>
                        setBenefitsAmountData({
                          value,
                          options: undefined,
                        })
                      }
                      name="max-limit-amount"
                      size="medium"
                      validationState={
                        errors.maxLimitAmount || errors.maxLimitPercentage ? 'error' : 'none'
                      }
                      isDisabled={isEditFlow && isNPSComponent}
                      errorText={errors.maxLimitAmount || errors.maxLimitPercentage}
                    />
                  </Box>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" width={'50%'}>
                  <Box flex={1} flexDirection={'row'} gap="spacing.3" display={'flex'}>
                    <Box flex={1}>
                      <TextInput
                        isDisabled={isEditFlow && isNPSComponent}
                        value={multipleOptionInputValue || ''}
                        prefix="₹"
                        type="number"
                        label=""
                        accessibilityLabel="Benefit value"
                        onChange={({ value }) => {
                          setMultipleOptionInputValue(value || '');
                          if (errors.multipleValuesList) {
                            clearFieldError('multipleValuesList');
                          }
                        }}
                        validationState={errors.multipleValuesList ? 'error' : 'none'}
                        errorText={errors.multipleValuesList}
                      />
                    </Box>
                    <Button
                      variant="secondary"
                      icon={PlusIcon}
                      color="white"
                      size="medium"
                      accessibilityLabel="Add benefit value"
                      onClick={onMultipleValueInputAdd}
                    />
                  </Box>
                  {isMultiValuesSelected ? (
                    <Box
                      borderRadius={'medium'}
                      borderColor={'surface.border.gray.muted'}
                      borderWidth={'thin'}
                      marginTop={'spacing.6'}>
                      <Box
                        paddingY={'spacing.4'}
                        paddingX={'spacing.6'}
                        borderBottomColor={'surface.border.gray.muted'}
                        borderBottomWidth={'thin'}>
                        <Text size="small" weight="semibold" color="surface.text.gray.muted">
                          Benefit values
                        </Text>
                      </Box>
                      <RadioGroup
                        name="options"
                        size="medium"
                        isDisabled={isEditFlow && isNPSComponent}
                        value={`${formData.benefit_amount_settings?.default_option_index}`}
                        onChange={({ value }) =>
                          setBenefitsAmountData({ default_option_index: Number(value) })
                        }>
                        {formData.benefit_amount_settings?.options?.map(
                          (option: number, index: number) => {
                            const isHovered = isHoveredOption === option;
                            return (
                              <MultiBenefitValueRow
                                key={option}
                                isHovered={isHovered}
                                onClick={() =>
                                  setBenefitsAmountData({ default_option_index: index })
                                }>
                                <Box display={'flex'} gap="spacing.3" alignItems={'center'}>
                                  <Radio value={String(index)} />
                                  <Text
                                    size="small"
                                    weight="semibold"
                                    color="surface.text.gray.subtle">
                                    {option}
                                  </Text>
                                  {formData.benefit_amount_settings?.default_option_index ===
                                    index && (
                                    <Box
                                      display={'flex'}
                                      flexDirection={'row'}
                                      gap="spacing.2"
                                      alignItems={'center'}>
                                      <Text
                                        size="xsmall"
                                        weight="semibold"
                                        color="surface.text.gray.muted">
                                        Default amount
                                      </Text>
                                      <Tooltip
                                        content="If an employee is assigned this flexi benefit, default value will automatically be declared unless employee updates the declaration"
                                        zIndex={1200}
                                        placement="top">
                                        <TooltipInteractiveWrapper
                                          display={'flex'}
                                          alignItems={'center'}>
                                          <InfoIcon
                                            size="xsmall"
                                            color="surface.icon.gray.subtle"
                                          />
                                        </TooltipInteractiveWrapper>
                                      </Tooltip>
                                    </Box>
                                  )}
                                </Box>
                                <Button
                                  size="xsmall"
                                  variant="secondary"
                                  icon={TrashIcon}
                                  color={isHovered ? 'negative' : 'white'}
                                  onClick={() =>
                                    setBenefitsAmountData({
                                      options: formData.benefit_amount_settings?.options?.filter(
                                        (o: number) => o !== option,
                                      ),
                                    })
                                  }
                                  onPointerEnter={() => {
                                    setIsHoveredOption(option);
                                  }}
                                  onMouseLeave={() => {
                                    setIsHoveredOption(-1);
                                  }}
                                  accessibilityLabel="delete"
                                />
                              </MultiBenefitValueRow>
                            );
                          },
                        )}
                      </RadioGroup>
                    </Box>
                  ) : null}
                  {isMultiValuesSelected && (
                    <Box marginTop="spacing.5">
                      <Text size="small" color="surface.text.gray.muted">
                        ✅ Employee will be able to select any of the above values for this
                        component: {formatOptions(formData?.benefit_amount_settings?.options || [])}
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
